import React, { useEffect, useRef, useState } from 'react';
import { createProductExportImageLoader } from '../utils/productExportImageClient';
import {
  LIVESTREAM_EXPORT_LIMITS,
  getLivestreamExportColumns,
  prepareLivestreamExport,
  buildLivestreamXlsx,
  downloadLivestreamXlsx,
} from '../utils/livestreamExport';

/** Caller supplies the current admin/staff authorization, never a saved role. */
export default function LivestreamExport({ rows = [], loadRows, listName = '', country, authorized = false, onClose }) {
  const isKorea = (country || rows[0]?.country || rows[0]?.product?.country) === 'korea';
  const [advanced, setAdvanced] = useState(false);
  const koreaSimple = isKorea && !advanced;
  const [selectedColumns, setSelectedColumns] = useState(() => getLivestreamExportColumns(isKorea ? 'korea' : country).map(column => column.key));
  const [stage, setStage] = useState('select');
  const [prepared, setPrepared] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const controllerRef = useRef(null);
  const dialogRef = useRef(null);
  const authorizedRef = useRef(authorized);
  const mountedRef = useRef(true);
  authorizedRef.current = authorized;
  useEffect(() => {
    const previousFocus = document.activeElement;
    mountedRef.current = true;
    dialogRef.current?.focus();
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
      previousFocus?.focus?.();
    };
  }, []);
  useEffect(() => {
    if (!authorized) controllerRef.current?.abort();
  }, [authorized]);

  const close = () => {
    controllerRef.current?.abort();
    onClose?.();
  };
  const handleDialogKey = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const controls = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]') || []);
    if (!controls.length) { event.preventDefault(); return; }
    const first = controls[0];
    const last = controls[controls.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (active === last || active === dialogRef.current)) {
      event.preventDefault(); first.focus();
    }
  };
  const toggleColumn = key => setSelectedColumns(current => current.includes(key)
    ? current.filter(value => value !== key)
    : [...current, key]);
  const inspect = async () => {
    if (!authorizedRef.current || stage === 'preparing' || stage === 'writing') return;
    const controller = new AbortController();
    controllerRef.current?.abort();
    controllerRef.current = controller;
    setError('');
    setProgress(typeof loadRows === 'function' ? { phase: 'reading' } : null);
    setAcknowledged(false);
    setStage('preparing');
    try {
      // Read the captured list afresh at the user's prepare action, then let the
      // synchronous snapshot freeze every field before image loading starts.
      // The loader may not support aborting its backend read, so recheck after it.
      const latestRows = typeof loadRows === 'function' ? await loadRows({ signal: controller.signal }) : rows;
      if (!mountedRef.current || controller.signal.aborted || !authorizedRef.current) return;
      if (!Array.isArray(latestRows)) throw new Error('無法取得最新清單資料，請重新讀取');
      setProgress(null);
      const next = await prepareLivestreamExport({
        rows: latestRows, listName, selectedColumns, format: koreaSimple ? 'korea-simple' : 'full', authorized: authorizedRef.current,
        signal: controller.signal,
        imageLoader: createProductExportImageLoader(),
        onProgress: value => { if (mountedRef.current && !controller.signal.aborted) setProgress(value); },
      });
      if (!mountedRef.current || controller.signal.aborted || !authorizedRef.current) return;
      setPrepared(next);
      setStage('review');
    } catch (failure) {
      if (!mountedRef.current || controller.signal.aborted) return;
      setError(failure.message || '匯出檢查失敗，請重試');
      setStage('select');
    }
  };
  const write = async () => {
    if (!authorizedRef.current || !prepared || stage !== 'review' || (prepared.issues.length && !acknowledged)) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    setStage('writing');
    setError('');
    try {
      const buffer = await buildLivestreamXlsx(prepared, { authorized: authorizedRef.current, signal: controller.signal });
      if (!mountedRef.current || controller.signal.aborted || !authorizedRef.current) return;
      downloadLivestreamXlsx(buffer, prepared.listName);
      setStage('done');
    } catch (failure) {
      if (!mountedRef.current || controller.signal.aborted) return;
      setError(failure.message || '檔案產生失敗，請重試');
      setStage('review');
    }
  };
  const failedImages = prepared?.issues.filter(issue => ['missing_image', 'image_failed'].includes(issue.code)).length || 0;
  const incompleteRows = new Set(prepared?.issues.filter(issue => !['missing_image', 'image_failed'].includes(issue.code)).map(issue => issue.rowIndex)).size;
  const busy = stage === 'preparing' || stage === 'writing';
  const buttonStyle = { padding: '0.65rem 1rem', cursor: 'pointer', borderRadius: 6, border: '1px solid #bac8cf', background: '#fff' };

  return (
    <div className="livestream-export-overlay" style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <section ref={dialogRef} tabIndex={-1} onKeyDown={handleDialogKey} role="dialog" aria-modal="true" aria-labelledby="livestream-export-title" style={{ width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: 12, padding: 24, color: '#172b35' }}>
        <h2 id="livestream-export-title" style={{ marginTop: 0 }}>匯出直播清單</h2>
        {!authorized ? <p role="alert">只有管理員與員工可以匯出清單</p> : (
          <>
            <p>{listName || '直播準備清單'} · {prepared && stage !== 'select' ? prepared.rows.length : rows.length} 個商品</p>
            {stage === 'select' && (
              <>

                {isKorea && <button type="button" onClick={() => setAdvanced(v => !v)} style={buttonStyle}>{advanced ? '使用韓國 12 欄簡版' : '進階：自選全欄位'}</button>}
                {koreaSimple ? <p>韓國 12 欄簡版；同售價、同成本的已選樣式合併顯示。</p> : <fieldset style={{ border: '1px solid #dce5e9', borderRadius: 8 }}>
                  <legend>匯出欄位</legend>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, padding: 8 }}>
                    {getLivestreamExportColumns(isKorea ? 'korea' : country).map(column => (
                      <label key={column.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="checkbox" checked={selectedColumns.includes(column.key)} onChange={() => toggleColumn(column.key)} />
                        {column.label}
                      </label>
                    ))}
                  </div>
                </fieldset>}
                <p style={{ color: '#526975', fontSize: 14 }}>每次最多 {LIVESTREAM_EXPORT_LIMITS.maxRows} 列。</p>
              </>
            )}
            {stage === 'preparing' && <p role="status">{progress?.phase === 'reading' ? '正在讀取最新清單資料…' : `正在檢查商品與圖片${progress ? `：${progress.completed} / ${progress.total}` : '…'}`}</p>}
            {stage === 'review' && prepared && (
              <>
                <p>已固定 {prepared.rows.length} 列、{prepared.columns.length} 個欄位。</p>
                {prepared.issues.length ? (
                  <div style={{ background: '#fff8e6', border: '1px solid #ead398', borderRadius: 8, padding: 16 }}>
                    <p role="status" style={{ marginTop: 0 }}>待補資料 {incompleteRows} 個商品；缺圖或圖片失敗 {failedImages} 項</p>
                    <ul aria-label="匯出前待確認事項" style={{ maxHeight: 220, overflow: 'auto', paddingLeft: 20 }}>
                      {prepared.issues.map((issue, index) => <li key={`${issue.rowIndex}-${issue.code}-${index}`} style={{ marginBottom: 6 }}>{issue.label}：{issue.message}</li>)}
                    </ul>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <input type="checkbox" checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} />
                      <span>我已確認以上待補資料，仍要匯出。失敗原因僅顯示於此，Excel 保留商品資料。</span>
                    </label>
                  </div>
                ) : <p role="status">資料與圖片檢查完成，可以匯出。</p>}
                <p style={{ color: '#526975', fontSize: 14 }}>{koreaSimple ? '售價同格合併不同金額；成本缺值留白，未填重量匯出為 0。' : '成本依原幣別匯出。'}</p>
              </>
            )}
            {stage === 'writing' && <p role="status">正在產生含嵌入圖片的 .xlsx…</p>}
            {stage === 'done' && <p role="status">已開始下載 .xlsx。請在下載資料夾查看檔案。</p>}
            {error && <p role="alert" style={{ color: '#a12626' }}>{error}</p>}
          </>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button type="button" onClick={close} style={buttonStyle}>{busy ? '取消' : '關閉'}</button>
          {authorized && stage === 'select' && <button type="button" style={buttonStyle} onClick={inspect} disabled={(typeof loadRows !== 'function' && !rows.length) || (!koreaSimple && !selectedColumns.length)}>檢查匯出資料</button>}
          {authorized && stage === 'review' && (
            <>
              <button type="button" style={buttonStyle} onClick={() => { setPrepared(null); setStage('select'); }}>重新選擇／讀取最新資料</button>
              <button type="button" style={{ ...buttonStyle, background: '#234a5c', color: '#fff' }} onClick={write} disabled={Boolean(prepared?.issues.length && !acknowledged)}>下載 .xlsx</button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
